import { useEffect, useMemo, useState } from "react";
import Carregando from "../componentes/Carregando";
import {
  AvisoEducacional,
  CAMPO_SIM,
  Campo,
  Cartao,
  ExplicacaoDaEtapa,
  Navegar,
  CabineDaRotina,
  PopupDaEtapa,
  Resumo,
  dicasDesligadas,
} from "../componentes/SimuladorMeiUi";
import { ETAPAS_DASN } from "../componentes/SimuladorMeiTextos";
import { filtrar, listarOcupacoes, type Ocupacao } from "../lib/ocupacoes";
import { cnpjValido, mascararCnpj, mascararMoeda } from "../lib/mascaras";
import {
  MESES,
  anosDaDasn,
  compor,
  dataHoraBr,
  excessoDoAno,
  type AtividadeEscolhida,
} from "../lib/dasMei";
import { gerarNumeroRecibo, lerParametrosDas, type ParametrosDas } from "../lib/simuladorMei";
import { htmlDoReciboDasn } from "../lib/documentosMei";

// A DASN-SIMEI: a declaração anual do MEI, em cinco passos.
//
// ---------------------------------------------------------------------------
// Duas coisas que mudaram em relação ao app de origem
// ---------------------------------------------------------------------------
// 1. **A lista de anos é derivada** (`anosDaDasn`). O original cravava
//    `2025 Declarar / 2024-2022 Retificar / 2021 Não Optante`; em 2027 aquela
//    tela convidaria o aluno a DECLARAR 2025, que já é retificação — ensinando o
//    prazo errado a quem está ali para aprender o prazo.
//
// 2. **Os tributos apurados saem da ATIVIDADE**, como no resto da ferramenta. O
//    original os deduzia de qual caixa de receita tinha valor
//    (`exibirCom = totCom > 0`), o que é outra regra para a mesma pergunta — e
//    duas regras para o mesmo número é o defeito que a casa paga desde a
//    000051. O que o MEI recolheu no ano vem do que ele está registrado para
//    fazer, não de onde ele digitou a receita.
export default function SimuladorDasn() {
  const [parametros, setParametros] = useState<ParametrosDas | null>(null);
  const [lista, setLista] = useState<Ocupacao[] | null>(null);
  const [erro, setErro] = useState(false);

  const [etapa, setEtapa] = useState<0 | 1 | 2 | 3 | 4>(0);
  const [cnpj, setCnpj] = useState("");
  const [erroCnpj, setErroCnpj] = useState<string | null>(null);
  const [nome, setNome] = useState("");

  const agora = useMemo(() => new Date(), []);
  const anos = useMemo(() => anosDaDasn(agora), [agora]);
  const [ano, setAno] = useState(() => anos[0].ano);

  const [receitaComercio, setReceitaComercio] = useState("");
  const [receitaServicos, setReceitaServicos] = useState("");
  const [teveEmpregado, setTeveEmpregado] = useState(false);

  const [caminhoneiro, setCaminhoneiro] = useState(false);
  const [atividades, setAtividades] = useState<AtividadeEscolhida[]>([]);
  const [busca, setBusca] = useState("");

  const [recibo, setRecibo] = useState<{ numero: string; data: string } | null>(null);

  const [semDicas] = useState(dicasDesligadas);
  const [popup, setPopup] = useState<number | null>(null);
  const [jaMostradas, setJaMostradas] = useState<number[]>([]);

  useEffect(() => {
    if (semDicas) return;
    if (jaMostradas.includes(etapa)) return;
    setPopup(etapa);
    setJaMostradas((v) => [...v, etapa]);
  }, [etapa, semDicas, jaMostradas]);

  useEffect(() => {
    void (async () => {
      const [p, o] = await Promise.all([lerParametrosDas(), listarOcupacoes()]);
      if (p === null || o === null) setErro(true);
      else {
        setParametros(p);
        setLista(o);
      }
    })();
  }, []);

  if (erro) {
    return (
      <Moldura>
        <p className="surface p-6 text-center text-sm leading-relaxed text-cinza">
          Não conseguimos carregar os valores do MEI e a lista de atividades agora. Recarregue a
          página; se continuar, fale com o suporte.
        </p>
      </Moldura>
    );
  }
  if (!parametros || !lista) return <Carregando />;

  // A máscara devolve as duas coisas separadas: o texto que o campo mostra e o
  // número que a conta usa. Somar o texto mascarado seria o defeito de sempre.
  const totalComercio = mascararMoeda(receitaComercio).numero ?? 0;
  const totalServicos = mascararMoeda(receitaServicos).numero ?? 0;
  const total = Math.round((totalComercio + totalServicos) * 100) / 100;

  const excesso = excessoDoAno(parametros, total, caminhoneiro);
  const composicao = compor(parametros, atividades, caminhoneiro);
  const situacao = anos.find((a) => a.ano === ano)?.situacao ?? "Declarar";
  const tipoDeclaracao = situacao === "Declarar" ? "Original" : "Retificadora";

  const linhas = MESES.map((_, i) => ({
    periodo: `${String(i + 1).padStart(2, "0")}/${ano}`,
    inss: composicao.inss,
    icms: composicao.icms,
    iss: composicao.iss,
    total: composicao.total,
  }));

  const somar = (campo: "inss" | "icms" | "iss" | "total") =>
    Math.round(linhas.reduce((s, l) => s + Math.round(l[campo] * 100), 0)) / 100;

  const transmitir = () => {
    setRecibo({ numero: gerarNumeroRecibo(), data: dataHoraBr(new Date()) });
    setEtapa(4);
  };

  const imprimirRecibo = () => {
    if (!recibo) return;
    const janela = window.open("", "_blank");
    if (!janela) {
      window.alert("Libere as janelas pop-up deste site para imprimir o recibo.");
      return;
    }
    janela.document.write(
      htmlDoReciboDasn({
        cnpj,
        nomeEmpresarial: nome.trim() || "EMPRESA FICTÍCIA MEI",
        ano,
        tipoDeclaracao,
        numeroRecibo: recibo.numero,
        dataTransmissao: recibo.data,
        receitaComercio: totalComercio,
        receitaServicos: totalServicos,
        receitaTotal: total,
        teveEmpregado,
        linhas,
      }),
    );
    janela.document.close();
  };

  const texto = ETAPAS_DASN[etapa];

  return (
    <Moldura
      passos={etapa > 0 ? ["Iniciar", "Preencher", "Resumo", "Conclusão"] : undefined}
      atual={etapa}
      // O herói da DASN é a RECEITA declarada, e o apoio diz a coisa que a rotina
      // ensina: se passou do teto, quanto passou. Só aparece depois de haver
      // receita — zero aqui não é resultado, é campo em branco.
      heroi={
        etapa > 0 && total > 0
          ? {
              rotulo: `Receita de ${ano}`,
              valor: reais(total),
              // A FAIXA é o que a rotina ensina, e ela tem três estados — não
              // dois: até o teto, até 20% acima (o MEI recolhe a diferença e
              // continua) e mais de 20% (desenquadramento retroativo). Reduzir a
              // "passou/não passou" apagaria justamente a distinção que faz a
              // DASN valer a pena.
              apoio:
                excesso.faixa === "dentro"
                  ? `Dentro do teto de ${reais(excesso.teto)}`
                  : `${reais(excesso.total - excesso.teto)} acima do teto${
                      excesso.faixa === "acima-de-20" ? " — mais de 20%" : " — até 20%"
                    }`,
            }
          : null
      }
      identidade={
        etapa > 0
          ? [
              ["CNPJ", cnpj],
              ["Nome empresarial", nome.trim() || "não informado"],
            ]
          : undefined
      }
    >

      {etapa === 0 && (
        <Cartao>
          <h2 className="font-display mb-1 text-lg font-bold text-titulo">DASN-SIMEI</h2>
          <p className="mb-5 text-sm leading-relaxed text-cinza">
            Declaração Anual do Simples Nacional para o MEI. Informe o CNPJ para começar.
          </p>
          <div className="max-w-md space-y-4">
            <Campo rotulo="CNPJ completo">
              <input
                className={`${CAMPO_SIM} font-mono`}
                inputMode="numeric"
                maxLength={18}
                value={cnpj}
                onChange={(e) => {
                  setCnpj(mascararCnpj(e.target.value));
                  setErroCnpj(null);
                }}
                placeholder="00.000.000/0000-00"
              />
              {erroCnpj && <p className="mt-1 text-xs text-erro-tinta">{erroCnpj}</p>}
            </Campo>
            <Campo rotulo="Nome empresarial (opcional)">
              <input
                className={CAMPO_SIM}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="como aparecerá no recibo"
              />
            </Campo>
          </div>
          <Navegar
            aoVoltar={null}
            aoAvancar={() => {
              if (!cnpjValido(cnpj)) {
                setErroCnpj(
                  "Este CNPJ não é válido: confira os catorze dígitos e os dois dígitos verificadores.",
                );
                return;
              }
              setErroCnpj(null);
              setEtapa(1);
            }}
            rotuloAvancar="Continuar"
          />
        </Cartao>
      )}

      {etapa === 1 && (
        <Cartao>
          <h2 className="font-display mb-4 text-lg font-bold text-titulo">Declarar ou retificar</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <Campo
              rotulo="Ano-calendário"
              ajuda="A declaração de um ano é entregue até 31 de maio do ano seguinte. Por isso o ano mais recente é declaração original, e os anteriores são retificação."
            >
              <select
                className={CAMPO_SIM}
                value={ano}
                onChange={(e) => setAno(Number(e.target.value))}
              >
                {anos.map((a) => (
                  <option key={a.ano} value={a.ano} disabled={a.bloqueado}>
                    {a.ano} — {a.situacao}
                    {a.bloqueado ? " (indisponível)" : ""}
                  </option>
                ))}
              </select>
            </Campo>
            <Campo rotulo="Tipo da declaração">
              <input className={`${CAMPO_SIM} bg-chip`} value={tipoDeclaracao} readOnly />
            </Campo>
          </div>
          <Navegar aoVoltar={() => setEtapa(0)} aoAvancar={() => setEtapa(2)} />
        </Cartao>
      )}

      {etapa === 2 && (
        <Cartao>
          <h2 className="font-display mb-1 text-lg font-bold text-titulo">
            Receita bruta anual de {ano}
          </h2>
          <p className="mb-5 text-xs text-cinza">
            Sem receita numa das atividades, informe zero — o campo não pode ficar em branco na
            declaração real.
          </p>

          <div className="grid gap-4">
            <Campo
              rotulo="Receita de comércio e indústria"
              ajuda="A receita bruta total do ano referente a comércio, indústria, serviço de transporte intermunicipal e interestadual e fornecimento de refeições."
            >
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                value={receitaComercio}
                onChange={(e) => setReceitaComercio(mascararMoeda(e.target.value).texto)}
                placeholder="R$ 0,00"
              />
              <span className="mt-1 block text-[11px] leading-snug text-cinza">
                Inclua também transporte intermunicipal e interestadual e fornecimento de
                refeições.
              </span>
            </Campo>

            <Campo
              rotulo="Receita de prestação de serviços"
              ajuda="A receita bruta total de serviços de qualquer natureza, exceto transportes intermunicipais e interestaduais. Inclua as ocupações de locador do Anexo XI da Resolução CGSN 140/2018 e demais receitas sem incidência de ISS e ICMS."
            >
              <input
                className={CAMPO_SIM}
                inputMode="numeric"
                value={receitaServicos}
                onChange={(e) => setReceitaServicos(mascararMoeda(e.target.value).texto)}
                placeholder="R$ 0,00"
              />
              <span className="mt-1 block text-[11px] leading-snug text-cinza">
                Exceto transporte intermunicipal e interestadual. Inclua locação e demais receitas
                sem incidência de ICMS e ISS.
              </span>
            </Campo>

            <Campo rotulo="Receita bruta total" ajuda="Preenchido pelo sistema: a soma dos dois campos.">
              <input
                className={`${CAMPO_SIM} bg-chip font-semibold`}
                value={reais(total)}
                readOnly
                tabIndex={-1}
              />
            </Campo>
          </div>

          <FaixaDoTeto excesso={excesso} caminhoneiro={caminhoneiro} />

          <fieldset className="mt-5">
            <legend className="mb-2 text-sm font-bold text-titulo">
              Possuiu empregado durante o período?
            </legend>
            <div className="flex gap-5">
              {[
                { valor: true, rotulo: "Sim" },
                { valor: false, rotulo: "Não" },
              ].map((op) => (
                <label key={String(op.valor)} className="flex cursor-pointer items-center gap-2 text-sm text-tinta">
                  <input
                    type="radio"
                    name="empregado"
                    className="size-4 accent-laranja"
                    checked={teveEmpregado === op.valor}
                    onChange={() => setTeveEmpregado(op.valor)}
                  />
                  {op.rotulo}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="mt-6">
            <legend className="mb-1 text-sm font-bold text-titulo">
              Atividade registrada no ano
            </legend>
            <p className="mb-2 text-xs leading-relaxed text-cinza">
              É a atividade — e não a caixa de receita que você preencheu — que decide quais
              tributos o MEI recolheu no ano. Escolha para o resumo mostrar a apuração mês a mês.
            </p>
            <input
              className={CAMPO_SIM}
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="cabeleireiro, açougueiro, 4930-2/02…"
            />
            {busca.trim() !== "" && (
              <ul className="mt-1.5 max-h-48 space-y-1 overflow-y-auto rounded-xl border border-borda p-1.5">
                {filtrar(
                  lista.filter((o) => !o.vedada),
                  busca,
                )
                  .slice(0, 20)
                  .map((o) => (
                    <li key={o.id} data-atividade={o.atividade}>
                      <button
                        type="button"
                        onClick={() => {
                          setAtividades((a) =>
                            a.some((x) => x.atividade === o.atividade)
                              ? a
                              : [...a, { atividade: o.atividade, cnae: o.cnae, icms: o.icms, iss: o.iss }],
                          );
                          setBusca("");
                        }}
                        className="w-full rounded-lg px-2.5 py-2 text-left text-xs transition hover:bg-chip"
                      >
                        <span className="font-semibold text-titulo">{o.atividade}</span>
                        <span className="mt-0.5 block text-cinza">{o.cnae ?? "sem CNAE"}</span>
                      </button>
                    </li>
                  ))}
              </ul>
            )}
            <div className="mt-2 flex flex-wrap gap-2">
              {atividades.map((a) => (
                <span
                  key={a.atividade}
                  data-escolhida={a.atividade}
                  className="inline-flex items-center gap-1.5 rounded-full bg-chip px-2.5 py-1 text-xs text-chip-tinta"
                >
                  {a.atividade}
                  <button
                    type="button"
                    onClick={() => setAtividades((v) => v.filter((x) => x.atividade !== a.atividade))}
                    aria-label={`Remover ${a.atividade}`}
                    title={`Remover ${a.atividade}`}
                    className="font-bold"
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
            <label className="mt-3 flex cursor-pointer items-center gap-2 text-sm text-tinta">
              <input
                type="checkbox"
                className="size-4 accent-laranja"
                checked={caminhoneiro}
                onChange={(e) => setCaminhoneiro(e.target.checked)}
              />
              MEI Caminhoneiro{" "}
              <span className="text-xs text-cinza">(INSS de 12% e teto maior)</span>
            </label>
          </fieldset>

          <Navegar aoVoltar={() => setEtapa(1)} aoAvancar={() => setEtapa(3)} />
        </Cartao>
      )}

      {etapa === 3 && (
        <Cartao>
          <h2 className="font-display mb-4 text-lg font-bold text-titulo">Resumo da declaração</h2>

          <div className="grid gap-3 md:grid-cols-2">
            <Resumo
              titulo="Contribuinte"
              itens={[
                ["CNPJ", cnpj],
                ["Nome empresarial", nome.trim()],
                ["Ano-calendário", String(ano)],
                ["Tipo de declaração", tipoDeclaracao],
                ["Possuiu empregado", teveEmpregado ? "Sim" : "Não"],
              ]}
            />
            <Resumo
              titulo="Receita bruta"
              itens={[
                ["Comércio e indústria", reais(totalComercio)],
                ["Serviços", reais(totalServicos)],
                ["Total", reais(total)],
                ["Teto do ano", reais(excesso.teto)],
                ["Percentual do teto", `${excesso.percentual.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`],
              ]}
            />
          </div>

          <h3 className="mt-6 mb-2 text-sm font-bold text-titulo">Valores apurados em {ano}</h3>
          <div className="overflow-x-auto rounded-xl border border-borda">
            <table className="w-full text-xs">
              <caption className="sr-only">
                Tributos apurados em cada período de apuração de {ano}
              </caption>
              <thead className="bg-superficie-2 text-cinza">
                <tr>
                  <th scope="col" className="p-2 text-left">Período</th>
                  <th scope="col" className="p-2 text-left">Benefício INSS</th>
                  <th scope="col" className="p-2 text-right">INSS</th>
                  <th scope="col" className="p-2 text-right">ICMS</th>
                  <th scope="col" className="p-2 text-right">ISS</th>
                  <th scope="col" className="p-2 text-right">Valor apurado</th>
                  <th scope="col" className="p-2 text-right">Valor pago</th>
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => (
                  <tr key={l.periodo} className="border-t border-borda">
                    <td className="p-2 font-medium text-tinta">{l.periodo}</td>
                    <td className="p-2 text-cinza">Não</td>
                    <td className="p-2 text-right text-tinta">{reais(l.inss)}</td>
                    <td className="p-2 text-right text-tinta">{reais(l.icms)}</td>
                    <td className="p-2 text-right text-tinta">{reais(l.iss)}</td>
                    <td className="p-2 text-right font-semibold text-titulo">{reais(l.total)}</td>
                    <td className="p-2 text-right text-cinza">—</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-borda bg-superficie-2">
                  <td className="p-2 font-bold text-titulo">Total</td>
                  <td className="p-2" />
                  <td className="p-2 text-right font-bold text-titulo">{reais(somar("inss"))}</td>
                  <td className="p-2 text-right font-bold text-titulo">{reais(somar("icms"))}</td>
                  <td className="p-2 text-right font-bold text-titulo">{reais(somar("iss"))}</td>
                  <td className="p-2 text-right font-bold text-titulo">{reais(somar("total"))}</td>
                  <td className="p-2" />
                </tr>
              </tbody>
            </table>
          </div>

          {composicao.semAtividade && (
            <p className="mt-2 text-xs text-cinza">
              Nenhuma atividade escolhida: a tabela mostra só o INSS, porque ICMS e ISS dependem
              do ramo registrado. Volte e escolha a atividade para vê-los.
            </p>
          )}

          <AvisoEducacional>
            Nenhuma declaração será transmitida à Receita Federal.
          </AvisoEducacional>

          <Navegar
            aoVoltar={() => setEtapa(2)}
            aoAvancar={transmitir}
            rotuloAvancar="Transmitir declaração"
          />
        </Cartao>
      )}

      {etapa === 4 && recibo && (
        <Cartao>
          <div className="mb-6 text-center">
            <span
              aria-hidden="true"
              className="inline-grid size-14 place-items-center rounded-full bg-aviso-fundo text-2xl text-aviso-tinta"
            >
              ✓
            </span>
            <h2 className="font-display mt-3 text-xl font-bold text-titulo">
              Declaração transmitida
            </h2>
            <p className="mt-1 text-sm text-cinza">DASN-SIMEI {ano} — recibo educacional</p>
          </div>

          <div className="mx-auto grid max-w-xl gap-3 sm:grid-cols-2">
            <Resumo
              titulo="Recibo"
              itens={[
                ["Número", recibo.numero],
                ["Data e hora", recibo.data],
              ]}
            />
            <Resumo
              titulo="Declarante"
              itens={[
                ["CNPJ", cnpj],
                ["Nome", nome.trim()],
              ]}
            />
          </div>

          <div className="mt-6 flex justify-center">
            <button type="button" onClick={imprimirRecibo} className="pill-cta text-sm">
              Imprimir o recibo
            </button>
          </div>

          <AvisoEducacional>
            O recibo sai com marca d'água e não comprova entrega de declaração nenhuma.
          </AvisoEducacional>
        </Cartao>
      )}

      {texto && <ExplicacaoDaEtapa texto={texto} />}
      {popup !== null && texto && (
        <PopupDaEtapa
          texto={texto}
          passo={etapa === 0 ? 0 : etapa}
          total={4}
          aoFechar={() => setPopup(null)}
        />
      )}
    </Moldura>
  );
}

/**
 * As DUAS faixas de excesso do teto, e a diferença entre elas.
 *
 * A faixa vem de `excessoDoAno` — a mesma função pura que o teste cobre nas
 * fronteiras exatas (R$ 81.000,00, R$ 97.200,00 e um centavo além de cada). A
 * tela não recalcula: uma tela que só diga "passou do teto" esconde justamente a
 * diferença que o aluno precisa aprender, e uma que recalcule a faixa por fora
 * passa a discordar do número ao lado.
 */
function FaixaDoTeto({
  excesso,
  caminhoneiro,
}: {
  excesso: { teto: number; total: number; percentual: number; faixa: string };
  caminhoneiro: boolean;
}) {
  if (excesso.total === 0) return null;

  const qual = caminhoneiro ? "teto do MEI caminhoneiro" : "teto do MEI";

  if (excesso.faixa === "dentro") {
    return (
      <p className="mt-4 rounded-xl bg-aviso-fundo px-4 py-3 text-xs leading-relaxed text-aviso-tinta">
        {reais(excesso.total)} é{" "}
        {excesso.percentual.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}% do {qual} (
        {reais(excesso.teto)}). Dentro do limite.
      </p>
    );
  }

  if (excesso.faixa === "ate-20") {
    return (
      <p className="mt-4 rounded-xl border-l-4 border-alerta-tinta bg-alerta-fundo px-4 py-3 text-xs leading-relaxed text-alerta-tinta">
        <strong>Excesso de até 20%.</strong> {reais(excesso.total)} passou do {qual} (
        {reais(excesso.teto)}) sem chegar a {reais(Math.round(excesso.teto * 1.2 * 100) / 100)}. O
        MEI paga o DAS complementar sobre o excesso e passa a ME a partir de janeiro do ano
        seguinte.
      </p>
    );
  }

  return (
    <p className="mt-4 rounded-xl border-l-4 border-erro-tinta bg-erro-fundo px-4 py-3 text-xs leading-relaxed text-erro-tinta">
      <strong>Excesso acima de 20%.</strong> {reais(excesso.total)} passou de{" "}
      {reais(Math.round(excesso.teto * 1.2 * 100) / 100)}, que é 120% do {qual}. O
      desenquadramento do SIMEI é <strong>retroativo a janeiro</strong> do próprio
      ano-calendário: os tributos do ano inteiro passam a ser apurados como ME.
    </p>
  );
}

function reais(v: number): string {
  return v.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function Moldura({
  passos,
  atual,
  heroi,
  identidade,
  children,
}: {
  passos?: string[];
  atual?: number;
  heroi?: { rotulo: string; valor: string; apoio?: string } | null;
  identidade?: [string, string][];
  children: React.ReactNode;
}) {
  return (
    <main className="mx-auto max-w-4xl px-4 py-7 sm:px-6">
      <CabineDaRotina
        rotulo="DASN-SIMEI"
        titulo="Declaração Anual (DASN-SIMEI)"
        descricao="A declaração do ano anterior: receita por atividade, o que acontece acima do teto e o recibo de entrega."
        passos={passos}
        atual={atual}
        heroi={heroi}
        identidade={identidade}
      />
      <div className="mt-6">{children}</div>
    </main>
  );
}
